import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { ZipcodeMetrics, HeatmapConfig } from './types';
import { calculateColorScale, getColorForValue, getMetricValue, getOpacityForValue } from './utils';
import ZipcodeTooltip from './ZipcodeTooltip';

// Fix for default markers in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface ZipcodeHeatmapCoreProps {
  data: ZipcodeMetrics[];
  config: HeatmapConfig;
  onZipcodeClick?: (zipcode: ZipcodeMetrics) => void;
  className?: string;
}

const ZipcodeHeatmapCore: React.FC<ZipcodeHeatmapCoreProps> = ({
  data,
  config,
  onZipcodeClick,
  className = '',
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.CircleMarker[]>([]);

  const [tooltip, setTooltip] = useState<{
    zipcode: ZipcodeMetrics | null;
    position: { x: number; y: number };
    visible: boolean;
  }>({
    zipcode: null,
    position: { x: 0, y: 0 },
    visible: false,
  });

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Create map instance
    const map = L.map(mapRef.current, {
      center: [39.8283, -98.5795], // Center of US
      zoom: 4,
      zoomControl: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      boxZoom: true,
      keyboard: true,
    });

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map);

    mapInstanceRef.current = map;

    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update markers when data or config changes
  useEffect(() => {
    if (!mapInstanceRef.current || !data.length) return;

    const map = mapInstanceRef.current;

    // Clear existing markers
    markersRef.current.forEach(marker => map.removeLayer(marker));
    markersRef.current = [];

    // Filter data to only include zipcodes with coordinates
    const validData = data.filter(zipcode => zipcode.lat && zipcode.lng);

    if (!validData.length) return;

    // Calculate color scale for selected metric
    const colorScale = calculateColorScale(validData, config.selectedMetric);

    // Create markers for each zipcode
    const newMarkers = validData.map(zipcode => {
      const value = getMetricValue(zipcode, config.selectedMetric);
      const color = getColorForValue(value, colorScale);
      const opacity = getOpacityForValue(value, colorScale, config.minOpacity, config.maxOpacity);

      // Calculate radius with proper validation
      let radius = 8; // Default minimum radius
      if (colorScale.max > 0 && !isNaN(value) && isFinite(value) && value > 0) {
        const normalizedValue = value / colorScale.max;
        if (isFinite(normalizedValue) && normalizedValue > 0) {
          radius = Math.max(8, Math.min(20, Math.sqrt(normalizedValue) * 20));
        }
      }

      // Create circle marker
      const marker = L.circleMarker([zipcode.lat!, zipcode.lng!], {
        radius,
        fillColor: color,
        color: '#ffffff',
        weight: 2,
        opacity: 0.8,
        fillOpacity: opacity,
      });

      // Add event listeners
      marker.on('mouseover', (e) => {
        if (config.showTooltips) {
          const containerPoint = map.latLngToContainerPoint(e.latlng);
          const mapContainer = map.getContainer();
          const rect = mapContainer.getBoundingClientRect();

          const tooltipPosition = {
            x: e.originalEvent.clientX,
            y: e.originalEvent.clientY,
          };

          setTooltip({
            zipcode,
            position: tooltipPosition,
            visible: true,
          });
        }

        // Highlight on hover
        marker.setStyle({
          weight: 3,
          opacity: 1,
          fillOpacity: Math.min(opacity + 0.2, 1),
        });
      });

      marker.on('mouseout', () => {
        setTooltip(prev => ({ ...prev, visible: false }));

        // Reset style
        marker.setStyle({
          weight: 2,
          opacity: 0.8,
          fillOpacity: opacity,
        });
      });

      marker.on('click', () => {
        onZipcodeClick?.(zipcode);
      });

      marker.addTo(map);
      return marker;
    });

    markersRef.current = newMarkers;

    // Fit map to show all markers
    if (validData.length > 0) {
      const group = L.featureGroup(newMarkers);
      map.fitBounds(group.getBounds(), { padding: [20, 20] });
    }

  }, [data, config, onZipcodeClick]);

  // Hide tooltip when mouse leaves map
  const handleMouseLeave = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  return (
    <div className={`relative zipcode-heatmap-container ${className}`}>
      <div
        ref={mapRef}
        className="w-full"
        style={{ height: `${config.mapHeight}px` }}
        onMouseLeave={handleMouseLeave}
      />

      {/* Loading overlay */}
      {data.length === 0 && (
        <div className="absolute inset-0 bg-gray-50 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600">
              Loading map data...
            </p>
          </div>
        </div>
      )}

      {/* No data message */}
      {data.length > 0 && data.filter(z => z.lat && z.lng).length === 0 && (
        <div className="absolute inset-0 bg-gray-50 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">
              No geographic data available
            </p>
            <p className="text-xs text-gray-500">
              Zipcode coordinates could not be determined
            </p>
          </div>
        </div>
      )}

      {/* Tooltip */}
      {tooltip.zipcode && (
        <ZipcodeTooltip
          zipcode={tooltip.zipcode}
          selectedMetric={config.selectedMetric}
          position={tooltip.position}
          visible={tooltip.visible}
        />
      )}
    </div>
  );
};

export default ZipcodeHeatmapCore;
